import { Alert, Fade, styled, Typography, useTheme } from "@mui/material";
import Slide from "@mui/material/Slide";
import Icon from "@mui/material/Icon";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAnalyticsTracker } from "../../app/AnalyticsTracker";
import { EditorCtx } from "../../wysiwyg/EditorCtx";
import { JSONAPIServerError } from "../../dataAccess/JSONAPIServerError";
import { getJustificationByAlignment } from "../../builder/themeOptionsBuilder";
import type { Alignment, MuiColor, Size } from "../../types/theme";
import { Discount, Item, RedemptionType } from "../../dataAccess/api/item";
import { PublicComponentProps } from "./PublicComponentProps";
import { Subcomponents as DealDetailsLayoutSubcomponents } from "./DealDetailsLayout";
import DealForm from "../ui/DealForm";
import { ErrorCode, ServerError } from "../../dataAccess/ServerError";
import {
  RedemptionFlow,
  useConfiguration,
} from "../../dataAccess/api/configuration";
import { useUser } from "../../dataAccess/api/user";
import { BrowserTab, useNavigateNewTabSync } from "../../helpers/navigation";
import {
  GuestFlowRedemption,
  postContactSupport,
  postRedemption,
  postResendEmail,
  Redemption,
} from "../../dataAccess/api/deal";
import SkeletonLoader from "../ui/SkeletonLoader";
import EarningsBox from "../ui/EarningsBox";
import {
  createGuest,
  isGuestUser,
  resendGuestUserVerification,
} from "../../dataAccess/api/guestUser";
import { replaceBracesKey, splitFullName } from "../../helpers/formatter";
import { lastDealKey } from "../pages/PDP";
import { ButtonProgress } from "../ui/ButtonProgress";
import { PreviewBannerCtx } from "../ui/PreviewBanner";
import LoginAnchorButton from "../common/LoginAnchorButton";
import {
  ActionsContainer,
  ActionsWrapper,
  animationTimeout,
  Content,
  DescriptionText,
  FlexTransitionGroup,
  InlineAlert,
  InnerContainer,
  LoadingContainer,
  Main,
  NameContainer,
  StyledBox,
  StyledButton,
  StyledCustomContainer,
  StyledDivider,
  StyledName,
} from "../ui/DealLikeComponents";
import TextAlert from "../ui/TextAlert";
import type { PanelComponentVariant, ExtendedPanelVariant } from "../ui/Panel";
import { useTranslation } from "../../helpers/i18n";
import Image from "../ui/Image";
import { SeeMoreButton } from "../ui/SeeMoreButton";
import {
  SEE_LESS,
  SEE_MORE,
  STATUS_CHECK_YOUR_INBOX,
  STATUS_PENDING_REVIEW,
  STATUS_PENDING_VERIFICATION,
} from "../../../theme/defaultConstants";
import Panel, { usePanelPadding } from "../ui/Panel";
import { PageStatusContext } from "../../app/PageStatusCtx";

enum RedemptionState {
  Loading,
  EmailFlow,
  OtherFlow,
  LoginRequired,
  EmailFormFlow,
}
interface Settings {
  redemptionActionsLayout: "normal" | "inverted";
  alignment: Exclude<Alignment, "center">;
  titleOrder: "aboveSavings" | "nextToSavings";
  savingAmountColor: string;
  newCustomersOnlyColor: string;
  newAndExistingCustomersColor: string;
  showSeparatorLine: boolean;
  hideAutogeneratedLabels: boolean;
  enableSeeMore: boolean;
  imageUrl: string;
  variant: ExtendedPanelVariant;
  headerSize: Size["size"];
  fullNamePlaceholderText?: string;
  companyNamePlaceholderText?: string;
  emailAddressPlaceholderText?: string;
  phoneNumberPlaceholderText?: string;
  backgroundColor: string;
  textColor: string;
  form: {
    alignment: Alignment;
    collectPhoneNumber: boolean;
    textBoxAbove: string;
    textBoxBelow: string;
    acknowledgmentHtml: string;
    formBackgroundColor: string;
    formTextColor: string;
  };
  statuses: {
    pendingVerification: string;
    pendingReview: string;
    checkYourInbox: string;
  };
  redemptionButtons: {
    emailRedemptions: {
      gated: string;
      redemption: string;
      postRedemption: string;
      successMessage: string;
      getApproval: string;
      resendApproval: string;
      claimNow: string;
    };
    urlRedemptions: {
      gated: string;
      redemption: string;
      postRedemption: string;
    };
    supportMessage: string;
    hideSupportButton: boolean;
  };
}

// TODO: remove "any" type in the next lines
interface Props
  extends PublicComponentProps<Settings, any, DealDetailsLayoutSubcomponents> {
  item?: Item;
  lastDealId?: string;
  loadingItem?: boolean;
  showRedemptionStatus?: boolean;
}

const StyledFlexContainer = styled("div")(() => ({
  display: "flex",
}));

const EmailFlowContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));

const TextContainer = styled("div")<{ variant: PanelComponentVariant }>(({
  theme,
  variant,
}) => {
  const { panelPaddingMobile, panelPaddingDesktop } = usePanelPadding(variant);
  return {
    padding: panelPaddingMobile,
    paddingBottom: `0 !important`,
    [theme.breakpoints.up("md")]: {
      padding: panelPaddingDesktop,
      flex: 1,
    },
  };
});

const StyledAverageSavings = styled(Typography)<Pick<Settings, "titleOrder">>(
  ({ theme, titleOrder }) => ({
    display: titleOrder === "nextToSavings" ? "inline-block" : "block",
    fontSize:
      titleOrder === "nextToSavings" ? "inherit" : theme.typography.h5.fontSize,
    color: theme.palette.success.main,
    marginBottom: "1.2rem",
  }),
);

type ValidationMessage = { color?: string };

const SuccessMessage = styled(Typography)<ValidationMessage>(
  ({ theme, color }) => ({
    color: color || theme.palette.success.main,
  }),
);

const WarningMessage = styled(Typography)<ValidationMessage>(
  ({ theme, color }) => ({
    color: color || theme.palette.warning.main,
  }),
);

const StatusIconContainer = styled("div")(() => ({
  marginRight: "0.8rem",
}));

const ActionSection = styled(Panel)<{
  variant: PanelComponentVariant;
  allowOverflow: boolean;
  removeSpacing: boolean;
}>(({ theme, variant, allowOverflow, removeSpacing }) => {
  const { panelPaddingMobile, panelPaddingDesktop } = usePanelPadding(variant);
  return {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    width: "100%",
    boxShadow: "none",
    overflow: allowOverflow ? "initial" : "hidden",
    marginTop: variant === "plain" ? theme.spacing(2) : undefined,
    padding: panelPaddingMobile,
    ...(removeSpacing ? { paddingTop: "0 !important" } : {}),
    [theme.breakpoints.up("md")]: {
      padding: panelPaddingDesktop,
      ...(removeSpacing ? { paddingTop: "0 !important" } : {}),
    },
  };
});

const StyledSeeMoreButton = styled(SeeMoreButton)(() => ({
  padding: 0,
}));

const StyledMain = styled(Main)(() => ({
  padding: `0 !important`,
}));

const ImageContainer = styled("div")<
  { variant: PanelComponentVariant; containedOnMobile: boolean } & Pick<
    Settings,
    "alignment"
  >
>(({ theme, variant, alignment, containedOnMobile }) => {
  const swiftAvailable =
    variant === "swift" || (variant === "swift-right" && alignment !== "right");
  const swiftLeft = variant === "swift" && alignment === "right";
  return {
    display: "flex",
    alignItems: "center",
    position: "relative",
    minHeight: 300,
    ...(!containedOnMobile
      ? {
          width: `calc(100% + ${theme.defaultSpacing.md.page.edges} * 2)`,
          left: `-${theme.defaultSpacing.md.page.edges}`,
        }
      : {}),
    [theme.breakpoints.up("md")]: {
      "& img": swiftAvailable
        ? {
            width: "50vw !important",
            left: swiftLeft ? "initial !important" : undefined,
          }
        : undefined,
      flex: 1,
      left: "initial",
      minHeight: "initial",
    },
  };
});

const DescriptionContainer = styled("div")<Pick<Settings, "alignment">>(
  ({ theme, alignment }) => ({
    display: "flex",
    gap: theme.spacing(2.2),
    justifyContent: getJustificationByAlignment(alignment),
    flexDirection: alignment === "right" ? "column" : "column-reverse",
    [theme.breakpoints.up("md")]: {
      flexDirection: alignment === "right" ? "row" : "row-reverse",
    },
  }),
);

const StyledRedemptionStatus = styled(Typography)<
  Pick<Settings["form"], "alignment">
>(({ alignment }) => ({
  display: "flex",
  alignContent: "center",
  alignItems: "center",
  marginTop: "1.5rem",
  width: "100%",
  gap: ".2rem",
  marginBottom: "1.5rem",
  justifyContent: getJustificationByAlignment(alignment),
}));

const StyledAlert = styled(Alert)(() => ({
  marginBottom: "1rem",
}));

const CenterContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  width: "100%",
  marginBottom: "1rem",
}));

const StyledSkeletonRectangular = styled(SkeletonLoader)(() => ({
  height: "5rem",
}));

const DefaultMessage = styled("span")(() => ({
  marginLeft: ".5rem",
  fontWeight: "bold",
}));

const NotificationMessage = styled(DefaultMessage)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const UpgradedMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: theme.typography.caption.fontSize,
  display: "inline-block",
}));

const BlurredBox = styled("div")(() => ({
  filter: "blur(4px)",
  pointerEvents: "none",
  userSelect: "none",
}));

const FormContainer = styled("div")(() => ({
  display: "flex",
  gap: "2rem",
  width: "100%",
  flexDirection: "column",
}));

const CustomColorIcon = styled(Icon)<{
  defaultColor: MuiColor;
  customColor?: string;
}>(({ theme, customColor, defaultColor }) => ({
  color: customColor || theme.palette[defaultColor]?.main,
}));

function useRedemtionFlow(): { state: RedemptionState } {
  const { user: realUser, loadingUser } = useUser();
  const { mocks } = useContext(EditorCtx);
  let user = realUser;
  if (mocks && "noUserMock" in mocks) {
    user = mocks?.noUserMock;
  }
  const { configuration, loadingConfiguration } = useConfiguration();

  if (loadingUser || loadingConfiguration) {
    return { state: RedemptionState.Loading };
  }

  if (configuration?.forceAccounts) {
    if (user?.id) {
      if (configuration?.redemptionFlow === RedemptionFlow.Email) {
        return { state: RedemptionState.EmailFlow };
      }
      return {
        state: RedemptionState.OtherFlow,
      };
    }
    return { state: RedemptionState.LoginRequired };
  }

  return { state: RedemptionState.EmailFormFlow };
}

export function checkIfEmailChanged(emailA?: string, emailB?: string) {
  return (
    emailA?.toLocaleLowerCase().trim() !== emailB?.toLocaleLowerCase().trim()
  );
}

const blurredDealMock: Discount = {
  id: "",
  title: "",
  activationType: RedemptionType.Email,
  contactEmail: "",
  instructions:
    "Fusce vel nunc orci. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas.",
  restrictions:
    "Proin in imperdiet augue. Sed eros orci, eleifend a faucibus sit amet, porta ut lectus.",
  averageTotalSavingsPrefix: "exact",
  averageTotalSavingsAmount: 0,
  averageTotalSavingsNotes: null,
  averageTotalSavingsCurrency: "$",
  slug: "",
  description:
    "Quisque vulputate, ipsum quis gravida ullamcorper, urna tellus dictum nibh, eget viverra urna justo eu turpis. Sed porttitor, dui vel porta gravida, mi dui maximus est, nec tempus tellus ante at arcu. Nulla sagittis accumsan nibh non luctus. Cras at risus nisl. Morbi imperdiet leo id ullamcorper euismod. Praesent sit amet lobortis odio. Morbi sollicitudin consectetur tincidunt.",
  newCustomersOnly: false,
  listing: "",
  categories: [],
  redemption: undefined,
};
const minTrimLength = 300;
function trimRecordValues<T extends string>(
  input: Record<T, { value: string }>,
  limit: number,
): { result: Record<T, { value: string }>; lastTrimmedKey: T | null } {
  const result: Record<T, { value: string }> = {} as any;
  let used = 0;
  let lastTrimmedKey: T | null = null;

  Object.keys(input).forEach((key) => {
    const { value } = input[key as T] || {};
    result[key as T] = { value: "" };
    if (!value || used >= limit) {
      return;
    }

    const available = limit - used;
    const sliced = value.slice(0, available);
    used += sliced.length;

    result[key as T] = { value: sliced?.trim() };

    if (sliced.length < value.length || used === limit) {
      lastTrimmedKey = key as T;
    }
  });

  return { result, lastTrimmedKey };
}

const Deal = ({
  item: initialItem,
  lastDealId,
  settings,
  siblingsComponents,
  loadingItem,
  showRedemptionStatus,
}: Props) => {
  const theme = useTheme();
  const { configuration } = useConfiguration();
  const { state } = useRedemtionFlow();
  const { user: realUser, loadingUser } = useUser();
  const { mocks } = useContext(EditorCtx);
  let user = realUser;
  if (mocks && "noUserMock" in mocks) {
    user = mocks?.noUserMock;
  }
  const [item, setItem] = useState(initialItem);
  const isBlurred =
    item?.blurredDeal && Object.keys(item.blurredDeal).length && !user?.id;
  const blurredId = item?.blurredDeal?.id;
  if (isBlurred) {
    item.deal = {
      ...blurredDealMock,
      ...item.blurredDeal,
      id: blurredDealMock.id,
    };
  }
  const dealId = String(item?.deal?.id);
  // TODO: Whenever the BE is ready, we must change this variable by the API request status
  const [mutating, setMutating] = useState(false);
  const [mutatingContactSupport, setMutatingContactSupport] = useState(false);
  const [contactSupportSuccess, setContactSupportSuccess] = useState(false);
  const [upgradedVisibility, setUpgradedVisibility] = useState(true);
  const [lastEmailVerificationId, setLastEmailVerificationId] = useState("");
  const { t } = useTranslation();
  const navigateNewTab = useNavigateNewTabSync();
  const [stateMessage, setStateMessage] = useState<ReactNode | null>(null);
  const [errorCode, setErrorCode] = useState<string>();
  const [warningMessage, setWarningMessage] = useState<ReactNode>(null);
  const guestUser = isGuestUser(user);
  const { isEditor, messages, setCurrentMessage } = useContext(EditorCtx);
  const { setPreviewParams } = useContext(PreviewBannerCtx);

  const deal = item?.deal;

  const { getPageDataState } = useContext(PageStatusContext) || {};
  const pageDataState = getPageDataState<{
    pendingGuestEmailVerification: boolean;
  }>?.();
  const pageData = pageDataState?.get();

  const redemption = deal?.redemption;
  const [redemptionJustRequested, setRedemptionJustRequested] = useState(false);

  const requestedAndNoLongerAllowed = useMemo(() => {
    const messageAvailable =
      showRedemptionStatus ||
      !configuration?.communityToggleSetting?.multipleRedemptionAllowed;
    return (
      (messageAvailable && redemption?.step === "requested") ||
      redemptionJustRequested
    );
  }, [
    configuration,
    redemption?.step,
    redemptionJustRequested,
    showRedemptionStatus,
  ]);

  const dealWasUpgraded =
    lastDealId &&
    deal?.id &&
    user?.id &&
    !guestUser &&
    lastDealId !== lastDealKey(item);
  const gatedButtonText =
    deal?.activationType === RedemptionType.Email
      ? settings.redemptionButtons?.emailRedemptions?.gated
      : settings.redemptionButtons?.urlRedemptions?.gated;
  const loading =
    loadingItem || loadingUser || (user?.id && deal?.id === blurredDealMock.id);
  const guestVerificationId = Number(deal?.guestEmailVerification);
  const formAlignment = settings.form?.alignment;

  const [contentExpanded, setContentExpanded] = useState(false);

  const coloredVariant =
    settings.variant !== "plain" && settings.variant !== "sleek";

  const {
    description,
    instructions,
    restrictions,
    seeMoreEnabled,
    showInstructionsLabel,
    showRestrictionsLabel,
    descriptionSeeMore,
    instructionsSeeMore,
    restrictionsSeeMore,
  } = useMemo(() => {
    const descriptionRaw = deal?.description || "";
    const restrictionsRaw = deal?.restrictions || "";
    const instructionsRaw = deal?.instructions || "";

    const showRestrictionsLabel = !settings.hideAutogeneratedLabels;
    const showInstructionsLabel = !settings.hideAutogeneratedLabels;

    const totalLength =
      descriptionRaw.length + restrictionsRaw.length + instructionsRaw.length;

    const seeMoreEnabled =
      settings.enableSeeMore && totalLength > minTrimLength + 100;

    if (contentExpanded || !seeMoreEnabled) {
      return {
        seeMoreEnabled,
        description: descriptionRaw,
        showRestrictionsLabel,
        restrictions: restrictionsRaw,
        showInstructionsLabel,
        instructions: instructionsRaw,
        descriptionSeeMore:
          seeMoreEnabled && !restrictionsRaw && !instructionsRaw,
        instructionsSeeMore: seeMoreEnabled && !restrictionsRaw,
        restrictionsSeeMore: seeMoreEnabled,
      };
    }

    const { result, lastTrimmedKey } = trimRecordValues(
      {
        description: { value: descriptionRaw },
        instructions: { value: instructionsRaw },
        restrictions: { value: restrictionsRaw },
      },
      minTrimLength,
    );
    const visibleRestrictionsLabel =
      showRestrictionsLabel && result.restrictions;
    const visibleInstructionsLabel =
      showInstructionsLabel && result.instructions;

    return {
      seeMoreEnabled,
      description: result.description.value,
      descriptionSeeMore: lastTrimmedKey === "description",
      showRestrictionsLabel: visibleRestrictionsLabel,
      restrictions: result.restrictions.value,
      restrictionsSeeMore: lastTrimmedKey === "restrictions",
      showInstructionsLabel: visibleInstructionsLabel,
      instructions: result.instructions.value,
      instructionsSeeMore: lastTrimmedKey === "instructions",
    };
  }, [deal, settings, contentExpanded]);

  const panelVariant: PanelComponentVariant = useMemo(() => {
    if (
      settings.variant === "swift" &&
      siblingsComponents?.MoreInformation?.settings.placement === "vertical"
    ) {
      return "swift-right";
    }
    return settings.variant;
  }, [settings.variant, siblingsComponents?.MoreInformation]);

  useEffect(() => {
    setItem(initialItem);
  }, [initialItem]);

  useEffect(() => {
    setLastEmailVerificationId(
      guestVerificationId ? String(guestVerificationId) : "",
    );
  }, [guestVerificationId]);

  useEffect(() => {
    if (!user?.guestUser) {
      if ((user && !user?.confirmed) || user?.guestUser) {
        setStateMessage(t("information_pending_guest_approval"));
      }
      if (
        (user?.confirmed && !user?.organization?.allowedInMarketplace) ||
        errorCode === ErrorCode.communityRequestPending
      ) {
        setStateMessage(
          replaceBracesKey(t("information_pending_organization_approval"), {
            key: "manager_organization_name",
            value: configuration?.managerOrganization.name,
          }),
        );
      }
    }
  }, [user, errorCode]);

  const { AnalyticsTracker } = useAnalyticsTracker();
  const handleRedemption = useCallback(
    (createdRedemption: Redemption, tab?: BrowserTab) => {
      if (!createdRedemption.step) {
        setErrorCode(ErrorCode.communityRequestPending);
        return;
      }
      AnalyticsTracker.trackIfDealRedemption();
      if (createdRedemption.landingPageUrl) {
        tab?.setLocation(createdRedemption.landingPageUrl);
      }
      if (item?.deal) {
        setItem({
          ...item,
          deal: {
            ...item.deal,
            redemption: createdRedemption,
          },
        });
      }
      setRedemptionJustRequested(true);
    },
    [item, setItem, setErrorCode, setRedemptionJustRequested, AnalyticsTracker],
  );

  const handleRedemptionError = useCallback(
    (e: unknown) => {
      if (e instanceof ServerError) {
        if (e instanceof JSONAPIServerError) {
          // These error are not translated. reason: very unlikely to happen or impossible due to flow prevent them before this
          console.error(e.getMessage());
          setStateMessage(e.getMessage());
        } else {
          console.error(e.getMessage());
          setWarningMessage(
            <StyledAlert severity="error">
              {t("information_generic_error")}
            </StyledAlert>,
          );
        }
        setErrorCode(String(e.getCode()));
      } else {
        throw e;
      }
    },
    [setStateMessage, setWarningMessage, setErrorCode, t],
  );

  const submitRedemption = async () => {
    if (isEditor) {
      setCurrentMessage(messages.previewAlert);
      return;
    }
    const tab = new BrowserTab();

    if (
      deal?.activationType === RedemptionType.Link &&
      state === RedemptionState.OtherFlow
    ) {
      tab.open();
    }
    setMutating(true);
    try {
      const { data } = await postRedemption(dealId).json();
      if (data) {
        handleRedemption(data as Redemption, tab); // When we resolve return types + errors in all endpoint, this included, we can remove this casted variable
      }
    } catch (e) {
      handleRedemptionError(e);
    }
    setMutating(false);
  };

  const onClaimNowClick = async () => {
    if (isEditor) {
      setCurrentMessage(messages.previewAlert);
      return;
    }
    if (user?.partnerOwned) {
      setPreviewParams({
        type: "deal",
        onPreview: () => {
          // TODO: this need to be discussed, to define how this preview will work in all its steps
        },
        onRequest: () => {
          submitRedemption();
        },
      });
    } else {
      await submitRedemption();
    }
  };

  const resendEmail = async () => {
    if (isEditor) {
      setCurrentMessage(messages.previewAlert);
      return;
    }
    setMutating(true);
    try {
      await postResendEmail(dealId).json();
      setContactSupportSuccess(false);
    } catch (e) {
      handleRedemptionError(e);
    }
    setMutating(false);
  };

  const sendContactSupport = async () => {
    if (isEditor) {
      setCurrentMessage(messages.previewAlert);
      return;
    }
    setMutatingContactSupport(true);
    try {
      await postContactSupport(dealId).json();
      setContactSupportSuccess(true);
    } catch (e) {
      setContactSupportSuccess(false);
      handleRedemptionError(e);
    }
    setMutatingContactSupport(false);
  };

  const submitGuestForm = useCallback(
    async (guestRedemption: GuestFlowRedemption) => {
      if (isEditor) {
        setCurrentMessage(messages.previewAlert);
        return;
      }
      setMutating(true);
      try {
        const hadEmailChanged = checkIfEmailChanged(
          user?.email,
          guestRedemption.email,
        );
        if (user?.confirmed && !hadEmailChanged) {
          const { data } = await postRedemption(dealId, guestRedemption).json();
          handleRedemption(data);
        } else {
          const { firstName, lastName } = splitFullName(
            guestRedemption.fullName,
          );
          const { fullName, termsAndConditions, ...guestData } =
            guestRedemption;

          if (lastEmailVerificationId && !hadEmailChanged) {
            await resendGuestUserVerification({
              verificationId: lastEmailVerificationId,
            }).json();
            pageDataState?.set({ pendingGuestEmailVerification: true });
          } else {
            const { data: createdGuest } = await createGuest({
              discountId: Number(dealId || blurredId),
              firstName,
              lastName,
              ...guestData,
            }).json();
            const verificationId =
              createdGuest?.guestEmailVerifications?.at(-1);
            if (verificationId) {
              setLastEmailVerificationId(verificationId);
              pageDataState?.set({ pendingGuestEmailVerification: true });
            }
          }
        }
      } catch (e) {
        handleRedemptionError(e);
      }
      setMutating(false);
    },
    [
      isEditor,
      messages.previewAlert,
      user,
      dealId,
      blurredId,
      lastEmailVerificationId,
      handleRedemption,
      setCurrentMessage,
      setMutating,
      setLastEmailVerificationId,
      handleRedemptionError,
      pageDataState,
    ],
  );

  const contactSupport = contactSupportSuccess ? (
    <InlineAlert
      icon={<Icon fontSize="inherit">check</Icon>}
      severity="success"
    >
      {settings.redemptionButtons?.supportMessage}
    </InlineAlert>
  ) : (
    <Slide
      direction="left"
      in
      mountOnEnter
      unmountOnExit
      timeout={animationTimeout}
    >
      <StyledButton
        startIcon={mutatingContactSupport ? <ButtonProgress /> : null}
        disabled={mutatingContactSupport}
        onClick={sendContactSupport}
        customVariant="secondary"
      >
        {t("information_contact_support")}
      </StyledButton>
    </Slide>
  );

  const loadingActionSkeleton = (
    <CenterContainer>
      <SkeletonLoader variant="rectangular" height="3rem" />
    </CenterContainer>
  );

  const afterResendEmail = (
    <Slide
      direction="left"
      in
      mountOnEnter
      unmountOnExit
      timeout={animationTimeout}
    >
      <StyledButton
        onClick={resendEmail}
        startIcon={mutating ? <ButtonProgress /> : null}
        disabled={mutating}
      >
        {replaceBracesKey(
          settings.redemptionButtons?.emailRedemptions?.postRedemption,
          {
            key: "seller_name",
            value: item?.seller.name,
          },
        )}
      </StyledButton>
    </Slide>
  );

  const postSentToInbox = !contactSupportSuccess ? (
    <Slide
      direction="left"
      in
      mountOnEnter
      unmountOnExit
      timeout={animationTimeout}
    >
      <InlineAlert
        icon={<Icon fontSize="inherit">check</Icon>}
        severity="success"
      >
        {settings.redemptionButtons?.emailRedemptions?.successMessage}
      </InlineAlert>
    </Slide>
  ) : (
    <></>
  );

  const postRedemptionFlow = (
    <ActionsWrapper alignment={formAlignment}>
      <FlexTransitionGroup
        redemptionActionsLayout={settings.redemptionActionsLayout}
      >
        {deal?.activationType === RedemptionType.Email ? (
          afterResendEmail
        ) : (
          <Slide
            direction="left"
            in
            mountOnEnter
            unmountOnExit
            timeout={animationTimeout}
          >
            <StyledButton
              onClick={() => {
                if (redemption?.landingPageUrl) {
                  navigateNewTab(redemption?.landingPageUrl);
                }
              }}
              startIcon={mutating ? <ButtonProgress /> : null}
              disabled={mutating}
            >
              {replaceBracesKey(
                settings.redemptionButtons?.urlRedemptions?.postRedemption,
                {
                  key: "seller_name",
                  value: item?.seller.name,
                },
              )}
            </StyledButton>
          </Slide>
        )}
        {!settings.redemptionButtons?.hideSupportButton && contactSupport}
        {deal?.activationType === RedemptionType.Email &&
          !mutating &&
          postSentToInbox}
      </FlexTransitionGroup>
    </ActionsWrapper>
  );

  const postEmailRedemptionFlow = loading ? (
    <SkeletonLoader />
  ) : (
    <>
      {!guestUser ? (
        <StyledCustomContainer alignment={formAlignment}>
          <ActionsWrapper alignment={formAlignment}>
            <ActionsContainer
              redemptionActionsLayout={settings.redemptionActionsLayout}
              variant={settings.variant}
            >
              {afterResendEmail}
              {!settings.redemptionButtons?.hideSupportButton && contactSupport}
              {!mutating && postSentToInbox}
            </ActionsContainer>
          </ActionsWrapper>
        </StyledCustomContainer>
      ) : (
        <></>
      )}
    </>
  );

  const emailFlow = (
    <ActionsWrapper alignment={formAlignment}>
      <ActionsContainer
        redemptionActionsLayout={settings.redemptionActionsLayout}
        variant={settings.variant}
      >
        {requestedAndNoLongerAllowed ? (
          postEmailRedemptionFlow
        ) : (
          <StyledButton
            onClick={onClaimNowClick}
            startIcon={mutating ? <ButtonProgress /> : null}
            disabled={mutating}
          >
            {settings.redemptionButtons?.emailRedemptions?.claimNow}
          </StyledButton>
        )}
        {warningMessage && contactSupport}
      </ActionsContainer>
    </ActionsWrapper>
  );

  const otherFlow = (
    <StyledCustomContainer alignment={formAlignment}>
      {requestedAndNoLongerAllowed ? (
        postRedemptionFlow
      ) : (
        <StyledButton
          startIcon={mutating ? <ButtonProgress /> : null}
          onClick={onClaimNowClick}
          disabled={mutating}
        >
          {deal?.activationType === RedemptionType.Email
            ? `${replaceBracesKey(
                settings.redemptionButtons?.emailRedemptions?.redemption,
                {
                  key: "seller_name",
                  value: item?.seller.name,
                },
              )}`
            : `${replaceBracesKey(
                settings.redemptionButtons?.urlRedemptions?.redemption,
                {
                  key: "seller_name",
                  value: item?.seller.name,
                },
              )}`}
        </StyledButton>
      )}
    </StyledCustomContainer>
  );

  const loginRequired = (
    <StyledCustomContainer alignment={formAlignment}>
      <LoginAnchorButton>{gatedButtonText}</LoginAnchorButton>
    </StyledCustomContainer>
  );

  const emailFormFlow = useMemo(() => {
    if (pageData?.pendingGuestEmailVerification && !mutating) {
      return (
        <TextAlert color="primary">
          {replaceBracesKey(
            settings?.statuses?.pendingVerification ||
              STATUS_PENDING_VERIFICATION,
            {
              key: "email",
              value: user?.email,
            },
          )}
        </TextAlert>
      );
    }
    if (redemption?.step === "pending_approval") {
      return (
        <TextAlert color="primary">
          {replaceBracesKey(
            settings?.statuses?.pendingReview || STATUS_PENDING_REVIEW,
            {
              key: "partner_name",
              value: configuration?.managerOrganization.name,
            },
          )}
        </TextAlert>
      );
    }
    if (requestedAndNoLongerAllowed) {
      return (
        <TextAlert color="primary">
          {replaceBracesKey(
            settings?.statuses?.checkYourInbox || STATUS_CHECK_YOUR_INBOX,
            {
              key: "partner_name",
              value: configuration?.managerOrganization.name,
            },
          )}
        </TextAlert>
      );
    }
    return (
      <EmailFlowContainer>
        <FormContainer>
          <DealForm
            coloredVariant={coloredVariant}
            redemptionButtons={settings.redemptionButtons}
            loading={mutating}
            guestUserCreated={!!lastEmailVerificationId}
            onSubmit={submitGuestForm}
            companyNamePlaceholderText={settings.companyNamePlaceholderText}
            emailAddressPlaceholderText={settings.emailAddressPlaceholderText}
            fullNamePlaceholderText={settings.fullNamePlaceholderText}
            phoneNumberPlaceholderText={settings.phoneNumberPlaceholderText}
            form={settings.form}
          />
        </FormContainer>
      </EmailFlowContainer>
    );
  }, [
    requestedAndNoLongerAllowed,
    item,
    pageData,
    lastEmailVerificationId,
    mutating,
    settings,
    submitGuestForm,
    user?.email,
    configuration,
    redemption,
    coloredVariant,
  ]);

  const states = {
    [RedemptionState.Loading]: loadingActionSkeleton,
    [RedemptionState.EmailFlow]: emailFlow,
    [RedemptionState.OtherFlow]: otherFlow,
    [RedemptionState.LoginRequired]: loginRequired,
    [RedemptionState.EmailFormFlow]: emailFormFlow,
  };

  const actionSection = states[state]; // TODO: check when we build skeleton loaders for all page, this may not be needed

  let actionSectionResult: ReactNode = actionSection;

  if (stateMessage) {
    // this code will not be like this, we need error status instead of checking the error message, BE is working on it
    if (
      errorCode === ErrorCode.communityRequestPending ||
      errorCode === ErrorCode.communityRequestRejected
    ) {
      actionSectionResult = (
        <TextAlert color="primary">{stateMessage}</TextAlert>
      );
    } else {
      actionSectionResult = (
        <TextAlert color="warning">{stateMessage}</TextAlert>
      );
    }
  }

  const savingsElement = useMemo(() => {
    if (!deal || !deal.averageTotalSavingsAmount) {
      return null;
    }
    return (
      <StyledAverageSavings variant="h5" titleOrder={settings.titleOrder}>
        <EarningsBox
          color={settings.savingAmountColor}
          averageTotalSavingsAmount={deal.averageTotalSavingsAmount}
          averageTotalSavingsPrefix={deal.averageTotalSavingsPrefix}
          averageTotalSavingsCurrency={deal.averageTotalSavingsCurrency}
        />
      </StyledAverageSavings>
    );
  }, [deal, settings]);

  if (!deal) {
    return null;
  }

  const seeMoreContent = seeMoreEnabled && (
    <>
      {!contentExpanded ? "... " : " "}
      <StyledSeeMoreButton
        aria-expanded={contentExpanded}
        onClick={() => setContentExpanded((prev) => !prev)}
      >
        {contentExpanded
          ? theme.settings.pagination.seeLessLabel || SEE_LESS
          : theme.settings.pagination.seeMoreLabel || SEE_MORE}
      </StyledSeeMoreButton>
    </>
  );

  const content = (
    <Content>
      <DescriptionText>
        {description}
        {descriptionSeeMore && seeMoreContent}
      </DescriptionText>
      {instructions && (
        <Typography>
          {showInstructionsLabel && (
            <>
              <StyledBox fontWeight={700} display="inline">
                {t("discounts_redemption_instructions")}
              </StyledBox>{" "}
            </>
          )}
          {instructions}
          {instructionsSeeMore && seeMoreContent}
        </Typography>
      )}
      {restrictions && (
        <Typography>
          {showRestrictionsLabel && (
            <>
              <StyledBox fontWeight={700} display="inline">
                {t("discounts_special_restrictions")}
              </StyledBox>{" "}
            </>
          )}
          {restrictions}
          {restrictionsSeeMore && seeMoreContent}
        </Typography>
      )}
      <StyledFlexContainer>
        {deal.newCustomersOnly ? (
          <>
            <StatusIconContainer>
              <CustomColorIcon
                customColor={settings.newCustomersOnlyColor}
                defaultColor="warning"
              >
                warning
              </CustomColorIcon>
            </StatusIconContainer>
            <WarningMessage color={settings.newCustomersOnlyColor}>
              {t("discounts_new_customers")}
            </WarningMessage>
          </>
        ) : (
          <>
            <StatusIconContainer>
              <CustomColorIcon
                customColor={settings.newAndExistingCustomersColor}
                defaultColor="success"
              >
                check_circle
              </CustomColorIcon>
            </StatusIconContainer>
            <SuccessMessage color={settings.newAndExistingCustomersColor}>
              {t("discounts_existing_customers")}
            </SuccessMessage>
          </>
        )}
      </StyledFlexContainer>
    </Content>
  );

  return (
    <StyledMain
      variant={panelVariant}
      backgroundColor={settings.backgroundColor}
      textColor={settings.textColor}
    >
      <InnerContainer>
        {loading ? (
          <LoadingContainer>
            <SkeletonLoader variant="text" width="30%" />
            <SkeletonLoader variant="text" width="30%" />
            <div>
              <SkeletonLoader variant="text" />
              <SkeletonLoader variant="text" />
            </div>
            <div>
              <SkeletonLoader variant="text" />
              <SkeletonLoader variant="text" />
            </div>
            <SkeletonLoader variant="text" />
            <StyledSkeletonRectangular variant="rectangular" />
          </LoadingContainer>
        ) : (
          <DescriptionContainer alignment={settings.alignment}>
            {settings.imageUrl && !contentExpanded && (
              <ImageContainer
                variant={panelVariant}
                alignment={settings.alignment}
                containedOnMobile={settings.variant !== "swift"}
              >
                <Image
                  src={settings.imageUrl}
                  alt=""
                  role="presentation"
                  fill
                  style={{ objectFit: "cover" }}
                />
              </ImageContainer>
            )}
            <TextContainer variant={panelVariant}>
              {warningMessage}
              <NameContainer>
                <StyledName size={settings.headerSize}>
                  {deal.title}{" "}
                  {settings.titleOrder === "nextToSavings" && savingsElement}
                </StyledName>
                {dealWasUpgraded && (
                  <Fade
                    in={upgradedVisibility}
                    addEndListener={() => {
                      setTimeout(() => {
                        setUpgradedVisibility(false);
                      }, 1000);
                    }}
                    timeout={{ exit: theme.transitions.duration.complex }}
                  >
                    <UpgradedMessage>
                      {" "}
                      {t("discounts_upgraded")}
                    </UpgradedMessage>
                  </Fade>
                )}
              </NameContainer>
              {settings.titleOrder !== "nextToSavings" && savingsElement}
              {isBlurred ? <BlurredBox>{content}</BlurredBox> : content}
              <StyledDivider showSeparatorLane={settings.showSeparatorLine} />
              {dealWasUpgraded && (
                <StyledRedemptionStatus alignment={formAlignment}>
                  <Icon color="primary">star</Icon>
                  <NotificationMessage>
                    {t("discounts_better_deal")}
                  </NotificationMessage>
                </StyledRedemptionStatus>
              )}
            </TextContainer>
          </DescriptionContainer>
        )}
        <ActionSection
          variant={panelVariant}
          backgroundColor={settings.form?.formBackgroundColor}
          textColor={settings.form?.formTextColor}
          allowOverflow={settings.variant === "swift"}
          removeSpacing={!coloredVariant || !settings.form?.formBackgroundColor}
        >
          {actionSectionResult}
        </ActionSection>
      </InnerContainer>
    </StyledMain>
  );
};

export type DealSettings = Settings;
export default Deal;
